import {React, useEffect, useState, useContext} from 'react'
import { toast } from '../lib/toast.js';
import { useParams } from 'react-router-dom';
import GlobalContext from "../context/GlobalContext.js";
import getValidateOnboardingToken from '../functions/API/getValidateOnboardingToken';
import postCompleteOnboardingRegistration from '../functions/API/postCompleteOnboardingRegistration';
import PasswordStrengthChecker from '../components/PasswordStrengthChecker';

export default function OnboardingAccept() {

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const { token } = useParams();
  const [loadingspinner, setLoadingSpinner] = useState(false);
  const [validating, setValidating] = useState(true);
  const [inviteData, setInviteData] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [passwordSecure, setPasswordSecure] = useState(false);
  const {theme, updateTheme, themeCSS, settings} = useContext(GlobalContext)

  useEffect(() => {
    async function validateToken() {
      const response = await getValidateOnboardingToken(token);
      if (response.response.ok) {
        setInviteData(response.data);
        setTokenValid(true);
      } else {
        setTokenValid(false);
      }
      setValidating(false);
    }
    validateToken();
  }, [token]);

  async function handleSubmit() {
    setLoadingSpinner(true);

    if (!passwordSecure) {
      toast.error("Your password does not meet the minimum requirements");
      setLoadingSpinner(false);
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords don't match!");
      setLoadingSpinner(false);
      return;
    }

    const response = await postCompleteOnboardingRegistration(token, password, confirmPassword);
    if (response.response.ok) {
      toast.success('Account created successfully - redirecting to login');
      setTimeout(function(){
        window.location.href = '/login';
      }, 3000);
    } else {
      const errorMsg = response.data?.message || 'Registration failed. Please try again.';
      if (typeof errorMsg === 'object') {
        const messages = Object.values(errorMsg).flat();
        messages.forEach(m => toast.error(m));
      } else {
        toast.error(errorMsg);
      }
    }
    setLoadingSpinner(false);
  }

  if (validating) {
    return (
      <div className={"min-h-screen py-1 " + (themeCSS.divCSS)}>
        <div className="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-sm space-y-10 mt-10 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-700 mx-auto"></div>
            <p className="text-sm text-gray-500">Validating your invite...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className={"min-h-screen py-1 " + (themeCSS.divCSS)}>
        <div className="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
          <div className="w-full max-w-sm space-y-10 mt-10">
            <div>
              {settings.logo_url && (
                <img
                  src={theme === 'dark' && settings.dark_mode_logo_url ? settings.dark_mode_logo_url : settings.logo_url}
                  alt="logo"
                  width="180"
                  className="mx-auto pb-2"
                />
              )}
              {!settings.logo_url && (
                <h1 className="pb-2 text-3xl font-semibold text-center">{settings.company_name}</h1>
              )}
              <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight">
                Invalid or Expired Link
              </h2>
              <p className="mt-4 text-center text-sm text-gray-500">
                This invite link is no longer valid. It may have expired or already been used.
                Please contact your housing provider for assistance.
              </p>
              <div className="mt-6 text-center">
                <a href="/login" className="text-sm font-semibold text-blue-700 hover:text-blue-500">
                  Go to login
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className={"min-h-screen py-1 " + (themeCSS.divCSS)}>
      <div className="flex min-h-full flex-1 items-center justify-center px-4 py-12 sm:px-6 lg:px-8">
        <div className="w-full max-w-sm space-y-10 mt-10">
          <div>
            {settings.logo_url && (
              <img
                src={theme === 'dark' && settings.dark_mode_logo_url ? settings.dark_mode_logo_url : settings.logo_url}
                alt="logo"
                width="180"
                className="mx-auto pb-2"
              />
            )}
            {!settings.logo_url && (
              <h1 className="pb-2 text-3xl font-semibold text-center">{settings.company_name}</h1>
            )}
            <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight">
              Welcome, {inviteData.first_name}!
            </h2>
            <p className="mt-2 text-center text-sm text-gray-500">
              Set a password to create your account.
            </p>
          </div>

          <form className="space-y-6" action="#" method="POST">
            <div className="relative -space-y-px rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-inset ring-gray-300" />
              <div>
                <label htmlFor="email-address" className="sr-only">Email address</label>
                <input
                  type="email"
                  id="email-address"
                  name="email"
                  value={inviteData.email}
                  disabled
                  className="relative block bg-gray-50 w-full rounded-md border-0 py-1.5 text-gray-500 ring-1 ring-inset ring-gray-200 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            <div className="relative -space-y-px rounded-md shadow-sm">
              <div className="pointer-events-none absolute inset-0 rounded-md ring-1 ring-inset ring-gray-300" />
              <div>
                <input
                  type="password"
                  name="password"
                  required
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="relative block bg-white/5 w-full rounded-t-md border-0 py-1.5 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                />
              </div>
              <div>
                <input
                  type="password"
                  name="password_confirmation"
                  required
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="relative block bg-white/5 w-full rounded-b-md border-0 py-1.5 ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-blue-700 sm:text-sm sm:leading-6"
                />
              </div>
            </div>
            <div>
              <PasswordStrengthChecker password={password} passwordSecure={setPasswordSecure}/>
            </div>
            <div className="mt-6">
              {loadingspinner ?
                <button type='button' className="flex justify-center w-full rounded-md bg-blue-700 px-3 py-1.5 text-sm font-semibold leading-6 text-white hover:bg-blue-500">
                  <div role="status">
                    <svg aria-hidden="true" className="mr-2 w-8 h-6 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor"/>
                      <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill"/>
                    </svg>
                  </div>
                </button>
              :
                <button type='button' onClick={() => {handleSubmit();}} className="flex w-full justify-center rounded-md bg-blue-700 px-3 py-1.5 text-sm font-semibold leading-6 text-white hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-700">
                  Create Account
                </button>
              }
            </div>
          </form>
        </div>
      </div>
    </div>
    </>
  )
}
